/** Read-only views derived from a founder's detail. Pure. */
import type { FounderDetail, Run, SizeOffer } from './schemas'
import { checkSubdomain } from './subdomains'

export interface Placement {
  reason: string
  size?: string
  region?: string
  runId: string
}

/** The placement the box step recorded on the latest run that has one (runs arrive newest first). */
export function placementFor(runs: Run[]): Placement | null {
  for (const run of runs) {
    const ids = run.steps.find((s) => s.key === 'box')?.ids
    if (ids && typeof ids.placement === 'string') {
      return {
        reason: ids.placement,
        size: typeof ids.size === 'string' ? ids.size : undefined,
        region: typeof ids.region === 'string' ? ids.region : undefined,
        runId: run.id,
      }
    }
  }
  return null
}

export function latestRun(detail: Pick<FounderDetail, 'runs'>): Run | null {
  return detail.runs[0] ?? null
}

/** The founder's live app domain: a custom domain first, then the platform one. */
export function appDomain(detail: Pick<FounderDetail, 'apps'>): string | null {
  const live = detail.apps.filter((a) => a.status !== 'removed')
  for (const a of live) if (a.customDomain) return a.customDomain
  for (const a of live) if (a.domain) return a.domain
  return null
}

export function activeBox(detail: Pick<FounderDetail, 'boxes'>) {
  return detail.boxes.find((b) => b.status !== 'destroyed') ?? null
}

/** Sizes offered in a region, cheapest first (the API already sorts by price). */
export function sizesInRegion(sizes: SizeOffer[], region: string): SizeOffer[] {
  return sizes.filter((s) => s.regions.includes(region))
}

/** A slug suggestion from a brand name, shaped to the API's rule: lowercase, digits and dashes, 3 to 32 long. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/, '')
    .slice(0, 32)
    .replace(/-+$/, '')
}

export const SLUG_RE = /^[a-z][a-z0-9-]{1,30}[a-z0-9]$/
export const REPO_RE = /^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/

/**
 * The message under the Subdomain field: our own check first, then the API's reason if it refused this exact value
 * (the API is the authority, so it wins when the two copies disagree). Empty means no custom domain, which is fine.
 */
export function subdomainError(subdomain: string, rejected: { subdomain: string; reason: string } | null): string | undefined {
  if (subdomain === '') return undefined
  const local = checkSubdomain(subdomain)
  if (!local.ok) return local.reason
  return rejected?.subdomain === subdomain ? rejected.reason : undefined
}

/**
 * Who already has this slug or subdomain, so the form says so before the API does. The API refuses a taken slug
 * (409); it doesn't check subdomains yet, and two founders on one subdomain would share an address.
 */
export function takenBy(founders: Pick<FounderDetail['founder'], 'name' | 'slug' | 'subdomain'>[], field: 'slug' | 'subdomain', value: string): string | null {
  if (!value) return null
  return founders.find((f) => f[field] === value)?.name ?? null
}
